import React, { Key, useEffect, useMemo, useState } from "react";
import { Button, Form, Row, Tooltip } from "antd";
import { useHistory } from "react-router";
import moment from "moment";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import ToggleableVersionsTable from "../../../../components/ToggleableVersionsTable/ToggleableVersionsTable";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import { TableColumn } from "../../../../lib/interfaces";
import { useAppDispatch, useAppSelector } from "../../../..";
import { checkIsLoading, toDisplayFormat, toHttpFormatStartOfDay } from "../../../../lib/utils";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import HistoryAndPropertyModal from "../../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import { useTypesCharacteristicsObjectContext } from "../../context/TypesCharacteristicsObjectContext/TypesCharacteristicsObjectContext";
import "./TypesCharacteristicsObjectListTable.less";

interface TableData {
  key?: Key;
  id?: number;
  name?: string;
  alias?: string;
  fromDate?: string;
  toDate?: string;
  accessModeId?: number;
  charTypeName?: string;
  objectTypeId?: number;
  period?: string;
  actions?: string;
  buttons?: string;
}

export const TypesCharacteristicsObjectListTable = () => {
  const history = useHistory();

  const dispatch = useAppDispatch();

  const { typesCharacteristicsObjectListSearchForm } = useTypesCharacteristicsObjectContext();

  const {
    typesCharacteristicsObject,
    typesCharacteristicsObjectLoading,
    typesCharacteristicsObjectObjectTypesDic,
    typesCharacteristicsObjectAccessModesDic,
  } = useAppSelector((state) => state.typesCharacteristicsObject);

  const [form] = Form.useForm();

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = (objectId: number) => {
    setHistoryModalVisible(true);
    setObjectId(objectId);
  };

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    name: "150px",
    alias: "150px",
    charTypeName: "150px",
    objectTypeId: "150px",
    accessModeId: "150px",
    period: "250px",
  };

  const getColumns = (): TableColumn<TableData>[] => [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      hidden: true,
    },

    {
      title: "Тип характеристики",
      dataIndex: "charTypeName",
      key: "charTypeName",
      width: columnsWidths.charTypeName,
      hidden: false,
    },

    {
      title: "Объект",
      dataIndex: "objectTypeId",
      key: "objectTypeId",
      width: columnsWidths.objectTypeId,
      hidden: false,
      render: (objectTypeId: number) =>
        versionsTableUtils.getDictionaryItemById(
          typesCharacteristicsObjectObjectTypesDic,
          objectTypeId
        )?.name,
    },

    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      width: columnsWidths.name,
      hidden: false,
    },
    {
      title: "Alias",
      dataIndex: "alias",
      key: "alias",
      width: columnsWidths.alias,
      hidden: false,
    },

    {
      title: "Доступ",
      dataIndex: "accessModeId",
      key: "accessModeId",
      width: columnsWidths.accessModeId,
      hidden: true,
      render: (accessModeId: number) =>
        versionsTableUtils.getDictionaryItemById(
          typesCharacteristicsObjectAccessModesDic,
          accessModeId
        )?.name,
    },

    {
      title: "Период",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
    },

    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: true,
    },

    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      className: "history-column history-column-end",
      style: { display: "flex", justifyContent: "end" },
      render: (value, record) => (
        <Row justify="end">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal(Number(record?.id));
            }}
            className="iconButton"
            type="text"
            htmlType="button"
            icon={<DotsIcon />}
          />
        </Row>
      ),
    },
  ];

  const [columns, setColumns] = useState<TableColumn<TableData>[]>(getColumns());

  // Обновление колонок
  useEffect(() => {
    setColumns(getColumns());
  }, [typesCharacteristicsObjectObjectTypesDic]);

  const tableData = useMemo((): TableData[] => {
    const data: TableData[] = typesCharacteristicsObject.map(
      ({
        name,
        id,
        alias,
        accessModeId,
        objectTypeId,
        charType,
        fromDate,
        toDate,
        charTypeId,
      }) => ({
        key: id,
        id,
        name,
        alias,
        accessModeId,
        charTypeName: charType?.name,
        objectTypeId,
        charTypeId,
        fromDate,
        toDate,
        period: `${toDisplayFormat(fromDate)} - ${toDisplayFormat(toDate)}`,
      })
    );
    return data;
  }, [typesCharacteristicsObject]);

  const onFinish = () => {};

  const getOnDate = () => {
    const onDateForm = typesCharacteristicsObjectListSearchForm.getFieldValue("onDate");

    return toHttpFormatStartOfDay(onDateForm || moment());
  };

  return (
    <div className="types-characteristics-list-table">
      <Form form={form} onFinish={onFinish}>
        <ToggleableVersionsTable
          tableName="policies-list-table"
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          childTableData={[]}
          isTableLoading={checkIsLoading(typesCharacteristicsObjectLoading)}
          isChildTableLoading={false}
          onRow={(record) => ({
            onClick: () => {
              history.push(
                `/types-characteristics-object/${record.objectTypeId}/${
                  record.id
                }?onDate=${getOnDate()}`
              );
            },
          })}
          isVisibleEye={false}
        />
      </Form>

      <HistoryAndPropertyModal
        objectTypeSysname="object_char_type"
        objectId={objectId}
        handleHideModal={() => {
          setHistoryModalVisible(false);
        }}
        visibleModal={historyModalVisible}
        // hasAccessToEdit={hasAccess(MENU_ADMIN_OFFERING_POLICY_WRITE)}
      />
    </div>
  );
};
